import { Texture } from 'pixi.js';

/**
 * Sprites are authored as ASCII character grids in TypeScript and baked to
 * textures at boot.
 *
 * No asset pipeline, no binary files, and every sprite is a diffable text
 * change in git. The cost is a few milliseconds of canvas work at startup,
 * which is invisible next to shader compilation.
 */

export type Palette = Readonly<Record<string, string>>;

/** Character reserved for transparency in every sprite. */
export const TRANSPARENT = '.';

export interface SpriteDefinition {
  readonly name: string;
  readonly rows: readonly string[];
  readonly palette: Palette;
}

export interface ParsedSprite {
  readonly width: number;
  readonly height: number;
  /** Row-major CSS colours; null is transparent. */
  readonly pixels: readonly (string | null)[];
}

/**
 * Validate and flatten an ASCII grid.
 *
 * Ragged rows and unmapped characters throw rather than rendering a subtly
 * wrong sprite — art bugs are far cheaper to find at boot than by eye.
 */
export function parseSprite(definition: SpriteDefinition): ParsedSprite {
  const { name, rows, palette } = definition;

  if (rows.length === 0) {
    throw new Error(`Sprite "${name}" has no rows`);
  }

  const width = rows[0]!.length;
  if (width === 0) {
    throw new Error(`Sprite "${name}" has zero-width rows`);
  }

  const pixels: (string | null)[] = [];

  for (const [y, row] of rows.entries()) {
    if (row.length !== width) {
      throw new Error(
        `Sprite "${name}" row ${y} is ${row.length} characters, expected ${width}`,
      );
    }
    for (const char of row) {
      if (char === TRANSPARENT) {
        pixels.push(null);
        continue;
      }
      const colour = palette[char];
      if (colour === undefined) {
        throw new Error(`Sprite "${name}" uses "${char}", which is not in its palette`);
      }
      pixels.push(colour);
    }
  }

  return { width, height: rows.length, pixels };
}

/** Draw a parsed sprite onto a canvas at 1 device pixel per sprite pixel. */
export function rasterise(sprite: ParsedSprite): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = sprite.width;
  canvas.height = sprite.height;

  const context = canvas.getContext('2d');
  if (!context) throw new Error('2D canvas context unavailable');

  const image = context.createImageData(sprite.width, sprite.height);
  for (let i = 0; i < sprite.pixels.length; i++) {
    const colour = sprite.pixels[i];
    if (!colour) continue;
    const { r, g, b, a } = parseColour(colour);
    const offset = i * 4;
    image.data[offset] = r;
    image.data[offset + 1] = g;
    image.data[offset + 2] = b;
    image.data[offset + 3] = a;
  }
  context.putImageData(image, 0, 0);

  return canvas;
}

export function bakeTexture(definition: SpriteDefinition): Texture {
  const texture = Texture.from(rasterise(parseSprite(definition)));
  // Nearest-neighbour: pixel art must never be smoothed on upscale.
  texture.source.scaleMode = 'nearest';
  texture.source.label = definition.name;
  return texture;
}

/** Bake a whole record of definitions, keeping the keys. */
export function bakeAll<K extends string>(
  definitions: Readonly<Record<K, SpriteDefinition>>,
): Record<K, Texture> {
  const result = {} as Record<K, Texture>;
  for (const key of Object.keys(definitions) as K[]) {
    result[key] = bakeTexture(definitions[key]);
  }
  return result;
}

interface Rgba {
  r: number;
  g: number;
  b: number;
  a: number;
}

/** Supports #rgb, #rrggbb and #rrggbbaa. */
export function parseColour(colour: string): Rgba {
  const hex = colour.replace('#', '');
  const expand = (value: string): number => parseInt(value.repeat(2), 16);

  if (hex.length === 3) {
    return { r: expand(hex[0]!), g: expand(hex[1]!), b: expand(hex[2]!), a: 255 };
  }
  if (hex.length === 6 || hex.length === 8) {
    return {
      r: parseInt(hex.slice(0, 2), 16),
      g: parseInt(hex.slice(2, 4), 16),
      b: parseInt(hex.slice(4, 6), 16),
      a: hex.length === 8 ? parseInt(hex.slice(6, 8), 16) : 255,
    };
  }
  throw new Error(`Unsupported colour "${colour}"`);
}
